import type {
  IndeedSendAttemptPayload,
  NotifyCandidateMetadata,
  NotifyCandidateMethod,
} from 'clients/CampaignsClient/CampaignsClient.types';
import type { ContactCandidateData, ContactCandidateParams } from '../useContactCandidateMutation.types';

import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { isIndeedData, isLinkedinData, isLinkedinInMailData } from '../utils/isChannelData';
import { useContactIndeedMutation } from './useContactIndeedMutation';
import { useContactLinkedinInMailMutation } from './useContactLinkedinInMailMutation';
import { useContactLinkedinMutation } from './useContactLinkedinMutation';

export const useGetContactChannel = () => {
  const contactIndeedMutation = useContactIndeedMutation();
  const contactLinkedinMutation = useContactLinkedinMutation();
  const contactLinkedinInMailMutation = useContactLinkedinInMailMutation();
  const currentUser = useCurrentUserQuery();

  return async <T extends NotifyCandidateMethod>(
    sendMethod: T,
    params: ContactCandidateParams<NotifyCandidateMethod>,
  ): Promise<{
    thread_id: string | undefined;
    metadata: NotifyCandidateMetadata | undefined;
    data: ContactCandidateData<T>;

    queued?: boolean;
    indeedSendAttemptPayload?: IndeedSendAttemptPayload;
  }> => {
    if (!currentUser) {
      throw new Error('Current user not found');
    }

    let thread_id = undefined;
    let metadata: NotifyCandidateMetadata | undefined = undefined;
    let queued = false;
    let indeedSendAttemptPayload: IndeedSendAttemptPayload | undefined;

    if (isIndeedData(sendMethod, params.data)) {
      const {
        thread_id: indeedThreadId,
        metadata: indeedMetadata,
        queued: indeedQueued,
        indeedSendAttemptPayload: indeedPayload,
      } = await contactIndeedMutation.mutateAsync({
        ...params.data,
        campaignId: params.campaignId,
      });

      thread_id = indeedThreadId;
      metadata = indeedMetadata;
      queued = indeedQueued;
      indeedSendAttemptPayload = indeedPayload;
    }

    if (isLinkedinData(sendMethod, params.data)) {
      const { thread_id: linkedinThreadId } = await contactLinkedinMutation.mutateAsync(params.data);

      thread_id = linkedinThreadId;
    }

    if (isLinkedinInMailData(sendMethod, params.data)) {
      const { thread_id: linkedinInMailThreadId } = await contactLinkedinInMailMutation.mutateAsync(params.data);

      thread_id = linkedinInMailThreadId;
    }

    return { thread_id, metadata, data: params.data as ContactCandidateData<T>, queued, indeedSendAttemptPayload };
  };
};
